'use client';

import { cn } from '../../utils/cn';
import './scroll-area.css';

type Orientation = 'vertical' | 'horizontal' | 'both';
type ScrollbarVisibility = 'auto' | 'always' | 'hover';

interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: Orientation;
  scrollbarVisibility?: ScrollbarVisibility;
}

const overflowMap: Record<Orientation, string> = {
  vertical: 'overflow-x-hidden overflow-y-auto',
  horizontal: 'overflow-y-hidden overflow-x-auto',
  both: 'overflow-auto',
};

const visibilityMap: Record<ScrollbarVisibility, string> = {
  auto: '',
  always: 'scroll-area-always',
  hover: 'scroll-area-hover',
};

function ScrollArea({
  className,
  orientation = 'vertical',
  scrollbarVisibility = 'hover',
  'aria-label': ariaLabel,
  children,
  ...props
}: ScrollAreaProps) {
  return (
    <div
      tabIndex={0}
      {...(ariaLabel ? { role: 'region', 'aria-label': ariaLabel } : {})}
      className={cn(
        'scroll-area relative',
        overflowMap[orientation],
        visibilityMap[scrollbarVisibility],
        'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface ScrollBarProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'vertical' | 'horizontal';
}

/**
 * Declarative scrollbar configuration element.
 * With CSS-first approach, this is primarily a semantic marker.
 * The actual scrollbar styling is handled by scroll-area.css pseudo-elements.
 */
function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: ScrollBarProps) {
  return (
    <div
      data-orientation={orientation}
      aria-hidden="true"
      className={cn('pointer-events-none absolute', className)}
      style={{ display: 'none' }}
      {...props}
    />
  );
}

export { ScrollArea, ScrollBar };
export type { ScrollAreaProps, ScrollBarProps };
